// Centralized auth facade with runtime lazy-loading to avoid importing auth0 in WebView

import React from 'react';
import { isRunningInWebView } from '../api/network/nativeBridge';
import { useAppAuthContext } from './context';
import { Auth0ProviderBypass } from './webview';
import { useAuth0Shim, withAuthenticationRequiredShim } from './webviewExports';
import type { Auth0ProviderOptions } from '@auth0/auth0-react';
import type { AppAuthShape } from './context';

const IN_WEBVIEW = isRunningInWebView();

// Lazy-loaded browser provider component (default export expected by React.lazy)
const LazyAuth0ProviderReal = React.lazy(() =>
  import('./browser').then(mod => ({ default: mod.Auth0ProviderReal }))
);

export function Auth0Provider(props: Auth0ProviderOptions) {
  if (IN_WEBVIEW) {
    // Only pass children to bypass provider to satisfy typings
    const { children } = props as unknown as { children?: React.ReactNode };
    const onlyChildren = {
      children,
    } as Record<string, unknown>;
    return React.createElement(Auth0ProviderBypass, onlyChildren);
  }
  return React.createElement(
    React.Suspense,
    { fallback: null },
    React.createElement(
      LazyAuth0ProviderReal as unknown as React.ComponentType<Auth0ProviderOptions>,
      props
    )
  );
}

// Choose hook implementation at module load to avoid conditional hook calls
const useAuth0Impl = IN_WEBVIEW ? useAuth0Shim : useAppAuthContext;

export function useAuth0(): AppAuthShape {
  return useAuth0Impl();
}

export function withAuthenticationRequired(
  Component: React.ComponentType<unknown>,
  options?: unknown
) {
  if (IN_WEBVIEW) {
    return withAuthenticationRequiredShim(Component, options);
  }
  const LazyWrapped = React.lazy(() =>
    import('./browserExports').then(mod => ({
      default: mod.withAuthenticationRequiredReal(
        Component as unknown as React.ComponentType<any>,
        options as any
      ),
    }))
  );
  return function Wrapped(props: Record<string, unknown>) {
    return React.createElement(
      React.Suspense,
      { fallback: null },
      React.createElement(LazyWrapped as any, props)
    );
  };
}
